import { Stack } from '@mui/material';
import { usePermissionsForCurrentUser } from '@retail/app/stores/selected-context';
import { CompetitorPricesSkeleton, SupplierItems } from '@retail/products/components';
import { MgItemCard, MgItemTabs } from '@retail/products/types';
import { ChipTabs, Paper } from '@shared/components';
import { useState } from 'react';
import { AssortmentTags, MemberAssortmentTags } from './AssortmentTags';
import { DocumentationContainer } from './DocumentationContainer';
import { useProductsTFunction } from '@retail/products/i18n';
import { MgItemCompetitorPrices } from './MgItemCompetitorPrices';
import { ProductInformationContainer } from './ProductInformationContainer';
import { SuspenseWithSentryErrorBoundary } from '@retail/components';

interface Props {
  mgItem: MgItemCard;
}
export function SupplierItemsDetails({ mgItem }: Props) {
  const t = useProductsTFunction();
  const { userCanRead, userCanUpdate } = usePermissionsForCurrentUser();

  const [currentTab, setCurrentTab] = useState<MgItemTabs>(MgItemTabs.PRODUCT_INFORMATION);

  const canEditAssortmentTags = userCanUpdate('CENTRAL_ASSORTMENT');

  const selectTab = (_: any, newTab: MgItemTabs) => setCurrentTab(newTab);

  const tabsConfig: { show: boolean; tab: { label: string; value: MgItemTabs } }[] = [
    {
      show: true,
      tab: { label: t('products.mgItem.details.title'), value: MgItemTabs.PRODUCT_INFORMATION },
    },
    {
      show: true,
      tab: { label: t('products.mgItem.supplierItem.packages'), value: MgItemTabs.PACKAGING },
    },
    {
      show:
        canEditAssortmentTags ||
        (userCanRead('CENTRAL_ASSORTMENT_MEMBER_VIEW') && userCanRead('SUB_ASSORTMENTS')),
      tab: { label: t('products.mgItem.assortments'), value: MgItemTabs.ASSORTMENTS },
    },
    {
      show: userCanRead('EXTENDED_MG_ITEM_INFO'),
      tab: { label: t('products.mgItem.documentation.title'), value: MgItemTabs.DOCUMENTATION },
    },
    {
      show: userCanRead('MARKET_INSIGHT'),
      tab: { label: t('products.prices.competitors.title'), value: MgItemTabs.COMPETITOR_PRICES },
    },
  ];

  const tabs = tabsConfig.filter((tab) => tab.show).map((tab) => tab.tab);

  const getTabContent = () => {
    if (!mgItem.getFirstSupplierItem()) {
      return <Paper>{t('products.mgItem.json.noSuppliersInfo')}</Paper>;
    }

    switch (currentTab) {
      case MgItemTabs.PRODUCT_INFORMATION:
        return (
          <ProductInformationContainer
            mgItem={mgItem}
            canReadExtendedItemInfo={userCanRead('EXTENDED_MG_ITEM_INFO')}
          />
        );
      case MgItemTabs.PACKAGING:
        return <SupplierItems mgItem={mgItem} />;
      case MgItemTabs.ASSORTMENTS:
        return canEditAssortmentTags ? (
          <AssortmentTags mgItemNumber={mgItem.mgItemNumber} />
        ) : (
          <MemberAssortmentTags mgItemNumber={mgItem.mgItemNumber} />
        );
      case MgItemTabs.DOCUMENTATION:
        return <DocumentationContainer mgItemNumber={mgItem.mgItemNumber} />;
      case MgItemTabs.COMPETITOR_PRICES:
        return (
          <SuspenseWithSentryErrorBoundary suspenseFallback={<CompetitorPricesSkeleton />}>
            <MgItemCompetitorPrices mgItemNumber={mgItem.mgItemNumber} />
          </SuspenseWithSentryErrorBoundary>
        );
      default:
        return null;
    }
  };

  return (
    <Stack flexGrow={1} overflow="hidden">
      <ChipTabs
        id="item"
        tabSize="small"
        tabs={tabs}
        currentTab={currentTab}
        variant="scrollable"
        onChange={selectTab}
      />
      <Paper tabPanel variant="outlined" sx={{ mt: -0.1, p: 0, height: '100%', overflow: 'auto' }}>
        {getTabContent()}
      </Paper>
    </Stack>
  );
}
